import React, { Component } from 'react';
import Chart from 'chart.js';
import './InventoryCheckCard.css';
import Card from '../../../components/card/Card';
import Label from '../../../components/label/Label';
import models from '../../../model/models';
import Input from '../../../inputs/input/Input';
import utils from '../../../utils/utils';
import mainAPI from '../../../api/MainAPI';
import debounce from 'lodash.debounce';
import emptyChart from './emptyChart.png';
import CardHeader from '../../../components/cardHeader/CardHeader';
import UniversalDropDown from '../../../components/universalDropDown/UniversalDropDown';

let thirtyDaysAgo = utils.xDaysAgo(30);
export default class InventoryCheckCard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			fromDate: thirtyDaysAgo,
			toDate: new Date(),
			unitId: 'all',
			startHour: '7',
			endHour: '12',
			earliestTimes: [],
			latestTimes: [],
			averageTimes: []
		};
	}

	componentDidMount = () => {
		// this._getData();
		if (this.props.userId) {
			this.userSubscribeId = models.user.onChange(() => {
				this._setData();
			});
		} else {
			this.subscribeId = models.box_check.onChange(debounce(this._setData, 1000));
		}
	};

	componentWillUnmount = () => {
		models.box_check.unsubscribe(this.subscribeId);
		models.user.unsubscribe(this.userSubscribeId);
	};

	_setData = async () => {
		let state = await this._getData();
		this.setState(state);
	};

	getFilter = (optional) => {
		let unitId = optional.unitId || this.state.unitId;
		let filter = {
			created_on: {
				values: [
					{ datetime: optional.toDate || this.state.toDate, operator: '<' },
					{
						datetime: optional.fromDate || this.state.fromDate,
						operator: '>'
					}
				]
			}
		};
		if (unitId && unitId !== 'all') {
			filter.unit_id = {
				values: [unitId]
			};
		}
		if (this.props.userId) {
			filter.created_by_id = {
				values: [this.props.userId]
			};
		}
		return filter;
	};

	_getData = async (optional = {}) => {
		const filter = this.getFilter(optional);
		filter.reseal_box_barcode_id = { isNull: true };
		const box_checks = await mainAPI.box_check.filter(filter);
		const user = models.user.obj(this.props.userId);
		models.box_check.setData(box_checks);
		let ready = false;
		if (box_checks) {
			ready = true;
			const groupByDate = box_checks.reduce((acc, check) => {
				const clientDateTime = utils.server_to_client_datetime(check.created_on);
				const day = utils.display_actual_date(clientDateTime);

				if (!acc[day]) {
					acc[day] = {
						checks: [], // original: box_checks object
						sum: 0,		// sum of all created_on values
						count: 0,	// count of box_checks objects that were created on this day
						average: 0,	// average of all created_on values
						latest: {created_on: Number.MIN_SAFE_INTEGER},
						earliest: {created_on: Number.MAX_SAFE_INTEGER}
					};
				}

				if(this._dateIsInRange(clientDateTime, optional)) {
					acc[day].count++;
					acc[day].sum += clientDateTime.getTime();
					if (check.created_on > acc[day].latest.created_on) { acc[day].latest = check }
					if (check.created_on < acc[day].earliest.created_on) { acc[day].earliest = check }
					acc[day].checks.push(check);
				}
				return acc;
			}, [])
			return { ready, user, ...this._calculateAggregates(groupByDate) };
		}
		return { ready, user};
	};

	_calculateAggregates = (groupByDate) => {
		const averageTimes = [];
		const latestTimes = [];
		const earliestTimes = [];
		for (const [date, {sum, count, latest, earliest}] of Object.entries(groupByDate)) {
			const t = new Date(date);

			const avg = sum / count;
			averageTimes.push({t, y: this._toTime(avg), time: utils.display_time_military(new Date(avg))});

			const latestTime = utils.server_to_client_datetime(latest.created_on);
			latestTimes.push({t, y: this._toTime(latestTime),
				name: this._userName(models.user.obj(latest.created_by_id)),
				time: utils.display_time_military(new Date(latestTime))});

			const earliestTime = utils.server_to_client_datetime(earliest.created_on);
			earliestTimes.push({t, y: this._toTime(earliestTime),
				name: this._userName(models.user.obj(earliest.created_by_id)),
				time: utils.display_time_military(new Date(earliestTime))});
		}
		return { averageTimes, latestTimes, earliestTimes };
	}

	_userName = ({first_name: first, last_name: last} = {first_name: '', last_name: ''}) =>
		`${first} ${last}`


	/*
	 * Checks if a given date is within the range of startHour and endHour.
	 *
	 * If the optional endHour and startHour are provided, they will take precedence.
	 *
	 * @param {Date} date - The date to check
	 * @param {Object} optional - An optional object that can contain startHour and endHour
	 * @return {Boolean} - True if the date is within the range, false otherwise
	 */
	_dateIsInRange = (date, optional) => {
		return date &&
			typeof date.getHours === 'function' &&
			( date.getHours() <= (optional.endHour || this.state.endHour) ||
				date.getHours() > (optional.startHour || this.state.startHour) )
	}

	_createChart = () => {
		if (!document.getElementById('daily-check-chart')) return;
		let ctx = document.getElementById('daily-check-chart').getContext('2d');
		document.getElementById('daily-check-chart').innerHTML = '';
		if (this.scatterChart) this.scatterChart.destroy();
		this.scatterChart = new Chart(ctx, {
			type: 'scatter',
			data: {
				datasets: [
					{
						label: 'Latest time',
						borderColor: '#333',
						borderWidth: '2',
						pointStyle: 'circle',
						radius: '5',
						pointHitRadius: '0',
						backgroundColor: '#FFFFFF',
						data: this.state.latestTimes
					},
					{
						label: 'Average',
						borderColor: '#309df1',
						backgroundColor: '#FFFFFF',
						borderWidth: '2',
						pointStyle: 'circle',
						radius: '5',
						pointHitRadius: '0',
						data: this.state.averageTimes
					},
					{
						label: 'Earliest time',
						backgroundColor: '#FFFFFF',
						borderColor: '#30fdf1',
						borderWidth: '2',
						pointStyle: 'circle',
						radius: '5',
						pointHitRadius: '0',
						data: this.state.earliestTimes
					}
				]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				legend: { labels: { usePointStyle: true } },
				axes: { display: false },
				xAxes: { display: false },
				tooltips: {
					callbacks: {
						label: function(tooltipItem, data) {
							if (!data.datasets[tooltipItem.datasetIndex]) return '';
							let time = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].time;
							let name = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].name;
							if (name && time) {
								return time + ' by ' + name;
							} else {
								return time + ' Average';
							}
						}
					}
				},
				scales: {
					yAxes: [{}],
					xAxes: [
						{
							type: 'time',
							time: { unit: 'day' },
							gridLines: {
								display: false
							}
						}
					]
				}
			}
		});
	};

	_toTime = (msTimeStamp) => {
		let dt = new Date(msTimeStamp);
		let hour = dt.getHours();
		let minutes = dt.getMinutes();
		if (minutes <= 9) {
			minutes = '0' + minutes;
		}
		let time24Hour = `0${hour}${minutes}`;
		return time24Hour;
		// return hour + parseFloat(minutes / 60);
	};

	_toDateChange = (toDate) => {
		if (!toDate) return;
		toDate = new Date(toDate);
		this._propertyChanged({ toDate });
	};

	_fromDateChange = (fromDate) => {
		if (!fromDate) return;
		fromDate = new Date(fromDate);
		this._propertyChanged({ fromDate });
	};

	_unitChanged = (unitId) => {
		console.log(unitId);
		this._propertyChanged({ unitId });
	};
	_startHourChanged = (startHour) => {
		console.log(startHour);
		this._propertyChanged({ startHour });
	};

	_endHourChanged = (endHour) => {
		this._propertyChanged({ endHour });
	};

	_propertyChanged = async (options) => {
		let state = await this._getData(options);
		for (let i in options) {
			state[i] = options[i];
		}
		this.setState(state);
	};

	_1_to_24 = () => {
		return {
			'24': '2400',
			'23': '2300',
			'22': '2200',
			'21': '2100',
			'20': '2000',
			'19': '1900',
			'18': '1800',
			'17': '1700',
			'16': '1600',
			'15': '1500',
			'12': '1200',
			'14': '1400',
			'13': '1300',
			'11': '1100',
			'10': '1000',
			'9': '0900',
			'8': '0800',
			'7': '0700',
			'6': '0600',
			'5': '0500',
			'4': '0400',
			'3': '0300',
			'2': '0200',
			'1': '0100'
		};
	};

	_renderEmptyMessage = () => {
		if (this._chartHasData()) return null;
		return (
			<div className="empty-message">
				<img src={emptyChart} alt="Nothing to Show"/>
				<Label larger black>
					Nothing to Show
				</Label>
				<Label black>Change the date or time range</Label>
			</div>
		);
	};

	_chartHasData = () => {
		if (!this.state.averageTimes || !this.state.latestTimes || !this.state.earliestTimes) return false;
		if ((this.state.averageTimes.length == 0 && this.state.latestTimes == 0) || this.state.earliestTimes == 0)
			return false;
		return true;
	};

	_chartClasses = () => {
		let classes = ' chart-container ';
		if (!this._chartHasData()) {
			classes += ' hidden ';
		}
		return classes;
	};

	render() {
		clearTimeout(this.timeout);
		this.timeout = setTimeout(() => {
			if (this.state.ready) {
				this._createChart();
			}
		}, 500);
		let title = 'Inventory checks';
		let { user = {} } = this.state;
		if (this.props.userId) {
			title = user.first_name + "'s checks";
		}
		return (
			<Card className="g-inventory-check-card">
				<div className="top-row">
					<CardHeader>{title}</CardHeader>
					<UniversalDropDown
						onBlur={() => {
							console.log('onBlur');
						}}
						onChange={this._startHourChanged}
						placeholder="Start Hour"
            persistantLabel
						options={this._1_to_24()}
						value={this.state.startHour}
					/>
					<UniversalDropDown
						onChange={this._endHourChanged}
						placeholder="End Hour"
            persistantLabel
						options={this._1_to_24()}
						value={this.state.endHour}
					/>
					<UniversalDropDown
						onChange={this._unitChanged}
            persistantLabel
						placeholder="Unit"
						options={{ all: 'All Units' }}
						endpoint={mainAPI.unit.allWithoutBox}
						value={this.state.unitId}
					/>

					<Input persistantLabel placeholder="Start" value={dateToString(this.state.fromDate)} type="date" onChange={this._fromDateChange} />
					<Input persistantLabel placeholder="End" value={dateToString(this.state.toDate)} type="date" onChange={this._toDateChange} />
				</div>
				<div className={this._chartClasses()}>
					<canvas id="daily-check-chart" />
				</div>
				{this._renderEmptyMessage()}
			</Card>
		);
	}
}

let dateToString = (date) => {
	try {
		return date.toISOString().substring(0, 10);
	} catch (e) {
		console.error(e);
		return '';
	}
};



// WEBPACK FOOTER //
// ./src/pages/dashboardPage/inventoryCheckCard/InventoryCheckCard.jsx